import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Gauge, UserPlus, Lock, ShieldCheck, Send, ArrowUpRight, KeyRound } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { AddChildForm } from '../welcome/AddChildrenStep';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** One wallet, and who is drawing on it. Adding a child is always allowed, it just runs the gate. */
export const FamilyAiPanel = () => {
  const { getAuthHeader } = useAuth();
  const [h, setH] = useState(null);
  const [adding, setAdding] = useState(false);
  const [capFor, setCapFor] = useState(null);
  const [capValue, setCapValue] = useState('');
  const [pwFor, setPwFor] = useState(null);
  const [pwValue, setPwValue] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/family/household`, getAuthHeader()).then(r => setH(r.data)).catch(() => setH(null));
  }, [getAuthHeader]);

  useEffect(load, [load]);

  const saveCap = async () => {
    try {
      const r = await axios.put(`${API}/family/children/${capFor.id}/cap`,
        { credits: capValue === '' ? null : Number(capValue) }, getAuthHeader());
      toast.success(r.data.message);
      setCapFor(null); setCapValue(''); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that limit');
    }
  };

  const resend = async (child) => {
    try {
      const r = await axios.post(`${API}/family/children/${child.id}/resend-invite`, {}, getAuthHeader());
      toast.success(`Invitation sent again to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend that');
    }
  };

  const savePassword = async () => {
    try {
      const r = await axios.post(`${API}/family/children/${pwFor.id}/set-login`,
        { password: pwValue }, getAuthHeader());
      toast.success(r.data.message);
      setPwFor(null); setPwValue(''); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not set that password');
    }
  };

  if (!h) return null;
  const pct = h.wallet.allowance
    ? Math.min(100, Math.round((1 - h.wallet.remaining / h.wallet.allowance) * 100)) : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-cyan-500/25 bg-slate-900/60 p-5 md:p-6"
      style={{ boxShadow: '0 0 40px -28px rgba(34,211,238,0.6)' }} data-testid="family-ai-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Gauge className="w-4 h-4 text-cyan-300" />
        <h2 className="font-outfit text-xl font-semibold text-white">Your family's AI capacity</h2>
        <span className="text-xs text-slate-300">{h.plan_name} · shared by everyone below</span>
        <Button onClick={() => setAdding(true)} data-testid="family-add-child"
          className="ml-auto rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold">
          <UserPlus className="w-4 h-4 mr-1.5" /> Add a child
        </Button>
      </div>

      <div className="mt-5">
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }}
            className="h-full rounded-full"
            style={{ background: pct > 85 ? '#fb7185' : 'linear-gradient(90deg,#22d3ee,#a78bfa)' }} />
        </div>
        <p className="text-xs text-slate-300 mt-2" data-testid="family-wallet-line">
          {pct}% of this month used · resets {h.wallet.cycle_ends
            ? new Date(h.wallet.cycle_ends).toLocaleDateString() : 'monthly'}
        </p>
      </div>

      <div className="mt-5 space-y-2">
        <div className="flex items-center gap-3 rounded-xl bg-slate-800/40 px-4 py-3">
          <span className="text-sm text-white font-medium">{h.me.name || 'You'}</span>
          <span className="text-xs text-slate-300">parent</span>
          <span className="text-xs text-slate-300 ml-auto fp-num">{h.me.used_this_cycle} used</span>
        </div>
        {h.children.map(c => (
          <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-slate-800/40 px-4 py-3"
            data-testid={`family-child-${c.id}`}>
            <span className="text-sm text-white font-medium">{c.full_name}</span>
            <span className="text-xs text-slate-300">{c.age}</span>
            {c.consent_on_file && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300/90">
                <ShieldCheck className="w-3 h-3" /> consent on file
              </span>
            )}
            <span className="text-xs text-slate-300">{c.access}</span>
            {c.login_enabled && !c.account_claimed && c.profile_status === 'invited' && (
              <button onClick={() => resend(c)} data-testid={`resend-${c.id}`}
                className="text-xs text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1">
                <Send className="w-3 h-3" /> resend invite
              </button>
            )}
            {c.login_enabled && !c.account_claimed && c.profile_status === 'setup_by_parent' && (
              <button onClick={() => { setPwFor(c); setPwValue(''); }} data-testid={`set-login-${c.id}`}
                className="text-xs text-emerald-300 hover:text-emerald-100 inline-flex items-center gap-1">
                <KeyRound className="w-3 h-3" /> change their password
              </button>
            )}
            <span className="text-xs text-slate-300 ml-auto fp-num">{c.used_this_cycle} used</span>
            <button onClick={() => { setCapFor(c); setCapValue(c.credit_cap_per_cycle || ''); }}
              data-testid={`cap-${c.id}`}
              className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1 border border-slate-700 rounded-full px-2.5 py-1">
              <Lock className="w-3 h-3" />
              {c.credit_cap_per_cycle ? `limit ${c.credit_cap_per_cycle}` : 'set a limit'}
            </button>
          </div>
        ))}
      </div>

      {h.plan_fit && !h.plan_fit.already_enough && (
        <div className="mt-5 rounded-xl border border-amber-400/30 bg-amber-500/[0.08] p-4" data-testid="family-plan-nudge">
          <p className="text-sm text-white font-medium">{h.plan_fit.headline}</p>
          <p className="text-xs text-slate-300/90 mt-1.5">
            Highly recommended: {h.plan_fit.recommended_plan_name} at ${h.plan_fit.recommended_price}/mo,             built to be shared across {h.plan_fit.people} people. {h.plan_fit.note}
          </p>
          <Button asChild className="mt-3 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold">
            <a href="/app/billing" data-testid="family-upgrade">
              Move to {h.plan_fit.recommended_plan_name} <ArrowUpRight className="w-4 h-4 ml-1" />
            </a>
          </Button>
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit text-xl">Add a child</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300 -mt-2">
            Same two steps every time: who they are, then their consent. They join your plan and
            share this wallet.
          </p>
          <AddChildForm compact={false} onAdded={() => { setAdding(false); load(); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!capFor} onOpenChange={(v) => !v && setCapFor(null)}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit text-lg">
              Monthly limit for {capFor?.first_name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300">
            How much of the family's monthly AI capacity {capFor?.first_name} can use. Leave it empty
            for no limit. They'll always keep everything they've already made.
          </p>
          <Input type="number" value={capValue} onChange={e => setCapValue(e.target.value)}
            placeholder="e.g. 400" className="input-dark" data-testid="cap-input" />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setCapFor(null)} className="text-slate-300">Cancel</Button>
            <Button onClick={saveCap} data-testid="cap-save"
              className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold">
              Save limit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!pwFor} onOpenChange={(v) => !v && setPwFor(null)}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit text-lg">
              {pwFor?.first_name}'s sign-in password
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300">
            You set this up for {pwFor?.first_name}, so you can change the password until they sign
            in for the first time. They'll sign in at {pwFor?.email} and choose their own password,             after that it's theirs and you'll see their FuturePath rather than edit it.
          </p>
          <Input type="password" value={pwValue} onChange={e => setPwValue(e.target.value)}
            placeholder="At least 8 characters" className="input-dark" data-testid="set-login-input" />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setPwFor(null)} className="text-slate-300">Cancel</Button>
            <Button onClick={savePassword} disabled={pwValue.length < 8} data-testid="set-login-save"
              className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
              Save password
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default FamilyAiPanel;
